import api from './api';
import { 
  CompanyResponse, 
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyFilters 
} from '../types/company';
import { ApiResponse } from '../types/api';
import { 
  sanitizeFormData, 
  sanitizeName, 
  sanitizeEmail, 
  sanitizePhone, 
  detectSqlInjection,
  transformCompanyDataForBackend 
} from '../utils/sanitize';

/**
 * Sanitise et transforme les données d'une entreprise avant envoi
 * - Détecte les injections SQL
 * - Sanitise les champs texte
 * - Transforme les dates en ISO 8601
 * - Normalise les valeurs d'enum pour le backend
 */
const sanitizeCompanyData = <T extends Record<string, any>>(data: T): T => {
  // Vérification des tentatives d'injection SQL
  const jsonString = JSON.stringify(data);
  if (detectSqlInjection(jsonString)) {
    console.warn('⚠️ Tentative d\'injection SQL détectée dans les données entreprise');
    throw new Error('Données invalides détectées. Veuillez vérifier vos entrées.');
  }

  // Sanitisation des champs texte
  const sanitized = sanitizeFormData(data, {
    raisonSociale: { maxLength: 200 },
    sigle: { maxLength: 50 },
    description: { maxLength: 5000 },
    email: { toLowerCase: true },
    telephone: {},
    adresse: { maxLength: 500 },
    siteWeb: { maxLength: 200 },
    // Ajoutez d'autres champs selon vos besoins
  } as any);

  // Transformation des données pour correspondre aux attentes du backend
  // (dates en ISO 8601, valeurs d'enum normalisées)
  const transformed = transformCompanyDataForBackend(sanitized);

  return transformed;
};

/**
 * Service pour la gestion des entreprises
 * ✨ SIMPLIFIÉ: Transmet TOUTES les données sans transformation
 * 🔒 SÉCURISÉ: Sanitisation des entrées contre les injections
 * Les formulaires sont la source de vérité
 */
class CompanyApiService {
  private baseUrl = '/companies';

  /**
   * Crée une nouvelle entreprise
   * @param data - TOUTES les données du formulaire EnterpriseIdentificationForm
   */
  async createCompany(data: CreateCompanyRequest): Promise<CompanyResponse> {
    // ✨ ANTI-PERTE: Log des champs tableau AVANT sanitisation
    console.log('📤 CompanyApiService.createCompany - Champs tableau AVANT sanitisation:', {
      secteursActiviteSecondaires: data.secteursActiviteSecondaires,
      secteursPersonalises: data.secteursPersonalises,
      produitsServices: data.produitsServices,
      dirigeants: data.dirigeants?.length,
      actionnaires: data.actionnaires?.length,
    });
    
    const sanitizedData = sanitizeCompanyData(data);
    
    // ✨ ANTI-PERTE: Log des champs tableau APRÈS sanitisation
    console.log('📤 CompanyApiService.createCompany - Champs tableau APRÈS sanitisation:', {
      secteursActiviteSecondaires: sanitizedData.secteursActiviteSecondaires,
      secteursPersonalises: sanitizedData.secteursPersonalises,
      produitsServices: sanitizedData.produitsServices,
      dirigeants: (sanitizedData as any).dirigeants?.length,
      actionnaires: (sanitizedData as any).actionnaires?.length,
    });
    
    console.log('📤 CompanyApiService.createCompany - Envoi de TOUTES les données:', sanitizedData);
    const response = await api.post<ApiResponse<CompanyResponse>>(this.baseUrl, sanitizedData);
    console.log('✅ CompanyApiService.createCompany - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * Récupère une entreprise par son ID
   */
  async getCompany(companyId: string): Promise<CompanyResponse> {
    console.log(`📥 CompanyApiService.getCompany - Récupération de l'entreprise ${companyId}`);
    const response = await api.get<CompanyResponse>(`${this.baseUrl}/${companyId}`);
    
    // ✨ ANTI-PERTE: Log des champs tableau reçus du backend
    console.log('📦 CompanyApiService.getCompany - Champs tableau reçus:', {
      secteursActiviteSecondaires: (response as any).secteursActiviteSecondaires,
      secteursPersonalises: (response as any).secteursPersonalises,
      produitsServices: (response as any).produitsServices,
      dirigeants: (response as any).dirigeants?.length,
      actionnaires: (response as any).actionnaires?.length,
    });
    
    // Le backend renvoie directement la company, pas { success, data }
    console.log('📦 Réponse brute getCompany:', response);
    return response;
  }

  /**
   * Met à jour une entreprise
   * @param data - TOUTES les données modifiées (partielles ou complètes)
   */
  async updateCompany(companyId: string, data: UpdateCompanyRequest): Promise<CompanyResponse> {
    // ✨ ANTI-PERTE: Log des champs tableau AVANT sanitisation
    console.log('📤 CompanyApiService.updateCompany - Champs tableau AVANT sanitisation:', {
      secteursActiviteSecondaires: data.secteursActiviteSecondaires,
      secteursPersonalises: data.secteursPersonalises,
      produitsServices: data.produitsServices,
    });
    
    const sanitizedData = sanitizeCompanyData(data);
    
    // ✨ ANTI-PERTE: Log des champs tableau APRÈS sanitisation
    console.log('📤 CompanyApiService.updateCompany - Champs tableau APRÈS sanitisation:', {
      secteursActiviteSecondaires: sanitizedData.secteursActiviteSecondaires,
      secteursPersonalises: sanitizedData.secteursPersonalises,
      produitsServices: sanitizedData.produitsServices,
    });
    
    console.log(`📤 CompanyApiService.updateCompany - Envoi de TOUTES les données pour ${companyId}:`, sanitizedData);
    const response = await api.patch<ApiResponse<CompanyResponse>>(`${this.baseUrl}/${companyId}`, sanitizedData);
    console.log('✅ CompanyApiService.updateCompany - Réponse brute:', response);
    
    // ✨ ROBUSTESSE: Gérer différentes structures de réponse du backend
    // Le backend peut retourner: { data: Company } ou directement Company
    const companyData = response?.data || response;
    
    if (!companyData || !companyData.id) {
      console.error('❌ Réponse invalide du backend - pas de données company:', response);
      throw new Error('Le serveur n\'a pas retourné les données de l\'entreprise mise à jour');
    }
    
    console.log('✅ CompanyApiService.updateCompany - Company extraite:', companyData.id);
    return companyData as CompanyResponse;
  }

  async deleteCompany(companyId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${companyId}`);
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadLogo() puis updateCompany({ logo: url })
   * Cette méthode est conservée pour compatibilité mais ne devrait plus être utilisée
   */
  async uploadLogo(companyId: string, logoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadLogo est déprécié. Utiliser cloudinaryService.uploadLogo() puis updateCompany()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${companyId}/logo`, 
      logoFile,
      'logo'
    );
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadCV() puis updateCompany()
   * Cette méthode est conservée pour compatibilité mais ne devrait plus être utilisée
   */
  async uploadOwnerCV(companyId: string, cvFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadOwnerCV est déprécié. Utiliser cloudinaryService.uploadCV() puis updateCompany()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${companyId}/owner/cv`, 
      cvFile,
      'cv'
    );
    return response.data;
  }

  async addLocation(companyId: string, locationData: {
    name: string;
    type: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${companyId}/locations`,
      locationData
    );
    return response.data;
  }

  async removeLocation(companyId: string, locationId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${companyId}/locations/${locationId}`
    );
    return response.data;
  }

  async addPartner(companyId: string, partnerData: {
    name: string;
    role: string;
    shares: number;
    email?: string;
    phone?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${companyId}/partners`,
      partnerData
    );
    return response.data;
  }

  async removePartner(companyId: string, partnerId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${companyId}/partners/${partnerId}`
    );
    return response.data;
  }

  async listCompanies(filters?: CompanyFilters): Promise<{
    companies: CompanyResponse[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get<ApiResponse<{
      companies: CompanyResponse[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>>(`${this.baseUrl}?${params.toString()}`);
    
    return response.data;
  }
}

export const companyApiService = new CompanyApiService();
export default companyApiService;